import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, formatNGN } from "@/lib/api";
import { BRAND_ASSETS } from "@/lib/brand";
import ProductCard from "@/components/ProductCard";
import PaymentBadges from "@/components/PaymentBadges";
import { usePageMeta } from "@/lib/seo";
import HeroSection from "@/components/home/HeroSection";
import TrustStrip from "@/components/home/TrustStrip";
import ComparisonSection from "@/components/home/ComparisonSection";
import FounderStorySection from "@/components/home/FounderStorySection";
import MissionCallout from "@/components/home/MissionCallout";
import SeeItInActionSection from "@/components/home/SeeItInActionSection";
import ProductionProofSection from "@/components/home/ProductionProofSection";
import WhyCoreSection from "@/components/home/WhyCoreSection";
import SavingsCalculator from "@/components/home/SavingsCalculator";
import SpecsSection from "@/components/home/SpecsSection";
import AudienceSection from "@/components/home/AudienceSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import EcosystemSection from "@/components/home/EcosystemSection";
import DistributorBand from "@/components/home/DistributorBand";
import FAQSection from "@/components/home/FAQSection";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [showBar, setShowBar] = useState(false);

  usePageMeta(
    null,
    "The CORE 500 dry iron runs on just 500W — about a quarter of the load of an ordinary iron. Engineered for solar & inverter homes in Nigeria. Shop with Paystack or WhatsApp."
  );

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subtle scroll-reveal on homepage sections (respects prefers-reduced-motion)
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const sections = document.querySelectorAll('[data-testid="home-page"] > section');
    if (!sections.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -60px 0px" }
    );
    sections.forEach((s) => {
      if (!s.previousElementSibling || s.id) return; // hero animates itself; anchor targets must not shift
      if (s.classList.contains("revealed")) return;
      s.classList.add("reveal");
      io.observe(s);
    });
    return () => io.disconnect();
  }, [products]);

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 560);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const core = products.find((p) => p.sku === "CORE-500");

  return (
    <div data-testid="home-page">
      <HeroSection core={core} />
      <MissionCallout />
      <FounderStorySection />
      <TrustStrip />

      {/* MARQUEE */}
      <section className="border-y" aria-hidden="true" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
        <div className="overflow-hidden py-4">
          <div className="marquee-track">
            {["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8"].map((id) => (
              <div key={id} className="flex items-center gap-12 px-12 whitespace-nowrap font-serif italic text-2xl" style={{ color: "var(--ink)" }}>
                <span className="sunset-text">Press on.</span>
                <span>The power stays on.</span>
                <span style={{ color: "var(--ink-muted)" }}>·</span>
                <span className="sunset-text">Press on.</span>
                <span>The power stays on.</span>
                <span style={{ color: "var(--ink-muted)" }}>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP — PRODUCT LINE (moved up so buyable products are visible early) */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="eyebrow">Shop SunPress</div>
              <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
                Available now. <br />
                <em className="italic" style={{ color: "var(--brand)" }}>The iron — and its perfect companion.</em>
              </h2>
            </div>
            <div className="md:col-span-5 text-[color:var(--ink-soft)]">
              The CORE 500 dry iron and the MistPro Spray Bottle — the complete pressing
              system, engineered for solar and inverter homes across West Africa.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      <ComparisonSection core={core} />
      <SeeItInActionSection />
      <ProductionProofSection />
      <WhyCoreSection />
      <SavingsCalculator corePrice={core?.price_ngn || 0} />
      <SpecsSection core={core} />
      <AudienceSection />
      <TestimonialsSection />

      {/* COMPLETE PRESSING SOLUTION — Ecosystem */}
      <section className="py-20 md:py-32" style={{ background: "var(--bg)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="eyebrow">Certified for solar &amp; backup power</div>
              <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
                The full family. <br />
                <em className="italic sunset-text">Engineered to share your inverter.</em>
              </h2>
            </div>
            <div className="md:col-span-5" style={{ color: "var(--ink-soft)" }}>
              Iron. Power Station. Sewing Machine. Standing Fan. Solar Blender. Every appliance
              optimised for solar and backup power systems — and certified to play nicely with
              the rest of your home.
            </div>
          </div>

          <div className="relative glossy rounded-2xl overflow-hidden">
            <img
              data-testid="family-certified"
              src={BRAND_ASSETS.family5Certified}
              alt="SunPress Solar Certified product family"
              className="w-full h-auto block"
              loading="lazy"
              style={{ background: "#fff" }}
            />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/products" data-testid="family-products-cta" className="btn-brand justify-center">
              See the full lineup <ArrowRight size={16} />
            </Link>
            <a data-testid="family-whatsapp" href="https://wa.me/2347072260372?text=I'd%20like%20to%20know%20more%20about%20SunPress%20Solar" className="btn-outline justify-center">
              Ask about SunPress Solar
            </a>
          </div>
        </div>
      </section>

      <EcosystemSection />

      {/* TAILOR STORY */}
      <section className="py-20 md:py-32 relative overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <div className="sunset-ambient" style={{ top: "-300px", right: "-200px", opacity: 0.6, animationDelay: "2s" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center relative z-10">
          <div className="md:col-span-6">
            <div className="glossy rounded-2xl overflow-hidden">
              <img
                src={BRAND_ASSETS.family4}
                alt="SunPress Solar low-watt product family"
                className="w-full h-auto block"
                loading="lazy"
                style={{ background: "#fff" }}
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="eyebrow">Built for real work</div>
            <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
              One power station.<br />
              <span className="sunset-text">Your whole workshop.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base sm:text-lg" style={{ color: "var(--ink-soft)" }}>
              From the tailor's iron to the studio fan, SunPress appliances are tuned to run on
              solar and inverter power — so the work doesn't stop when the grid does.
            </p>
            <Link to="/distributors" data-testid="story-distributor-cta" className="mt-10 inline-flex items-center gap-2 text-sm font-medium sunset-text py-2">
              Become a distributor <ArrowRight size={14} color="var(--sun-orange)" />
            </Link>
          </div>
        </div>
      </section>

      <DistributorBand />
      <FAQSection />

      {/* WARRANTY CTA */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="sunset-ambient" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 0.35 }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center relative z-10">
          <div className="eyebrow">12-month limited warranty</div>
          <h2 className="display mt-4 mx-auto max-w-3xl" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", color: "var(--ink)" }}>
            Built to earn a place in <span className="sunset-text">your home</span>.
          </h2>
          {core && (
            <>
              <div className="mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-4">
                <Link to="/products/CORE-500" data-testid="bottom-cta-shop" className="btn-brand justify-center">
                  Pre-order — {formatNGN(core.price_ngn)} <ArrowRight size={16} />
                </Link>
                <a data-testid="bottom-cta-whatsapp" href="https://wa.me/2347072260372" className="btn-outline justify-center">
                  Order on WhatsApp
                </a>
              </div>
              <div className="mt-6 flex justify-center">
                <PaymentBadges className="justify-center" />
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
                <Link to="/contact" data-testid="final-cta-contact" className="sunset-text font-medium py-2">Contact sales →</Link>
                <Link to="/distributors" data-testid="final-cta-distributor" className="sunset-text font-medium py-2">Find a distributor →</Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* STICKY MOBILE BUY BAR */}
      {core && (
        <div
          data-testid="sticky-cta-bar"
          className={`sticky-cta md:hidden ${showBar ? "visible" : ""}`}
          aria-hidden={!showBar}
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--ink-soft)" }}>CORE 500</div>
            <div className="font-medium font-mono text-sm" style={{ color: "var(--ink)" }}>{formatNGN(core.price_ngn)}</div>
          </div>
          <Link
            to="/products/CORE-500"
            data-testid="sticky-cta-buy"
            className="btn-brand justify-center"
            style={{ padding: "11px 22px" }}
            tabIndex={showBar ? 0 : -1}
          >
            Pre-order <ArrowRight size={15} />
          </Link>
        </div>
      )}
    </div>
  );
}
